import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScoreBreakdown } from '@/types/survey';
import { analyzeCompetitivePosition } from '@/services/aiAnalyticsService';
import { TrendingUp, TrendingDown, Zap, Award, BarChart2, Info, PieChart, Lightbulb } from 'lucide-react';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

interface CompetitiveAnalysisProps {
  scoreBreakdown: ScoreBreakdown | null;
  industry: string;
}

const CompetitiveAnalysis: React.FC<CompetitiveAnalysisProps> = ({ 
  scoreBreakdown, 
  industry 
}) => {
  // Generate competitive analysis based on score breakdown and industry
  const analysis = React.useMemo(() => {
    if (!scoreBreakdown) return null;
    
    return analyzeCompetitivePosition(scoreBreakdown, industry);
  }, [scoreBreakdown, industry]);

  if (!scoreBreakdown) {
    return <div className="text-center py-6">No data available for competitive analysis</div>;
  }

  // Data for score comparison visual
  const scoreComparison = {
    your: scoreBreakdown.overallScore * 10, // Convert to 0-100 scale
    average: getIndustryAverage(industry)
  };

  // Helper function to get industry average (using real data from analysis now)
  function getIndustryAverage(industry: string): number {
    if (analysis?.industryBenchmarks && analysis.industryBenchmarks.length > 0) {
      const avgScores = analysis.industryBenchmarks.map(item => item.industryAverage);
      return avgScores.reduce((sum, score) => sum + score, 0) / avgScores.length;
    }
    
    const averages: Record<string, number> = {
      'Technology': 65,
      'Healthcare': 68,
      'Finance': 63,
      'Education': 67,
      'Entertainment': 70,
      'Retail': 62,
      'Manufacturing': 58,
      'Energy': 60,
      'Hospitality': 65,
      'Food & Beverages': 63,
      'Web 3.0': 55,
      'Other': 60
    };
    
    return averages[industry] || 60;
  }
  
  // Format data for the benchmarks chart
  const prepareBenchmarkChartData = () => {
    if (!analysis || !analysis.industryBenchmarks) return [];
    
    return analysis.industryBenchmarks.map(item => ({
      category: item.category,
      your: item.yourScore,
      average: item.industryAverage,
      top: item.topPerformerScore
    }));
  };
  
  const benchmarkChartData = prepareBenchmarkChartData();
  
  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 shadow-md rounded text-sm">
          <p className="font-medium mb-1">{label}</p>
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.color }}
              ></div>
              <span>{entry.name}: {entry.value}%</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Competitive Analysis</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {analysis && (
            <>
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-700 flex items-center">
                    <BarChart2 className="mr-2 h-5 w-5 text-blue-500" />
                    <span>Industry Benchmarks</span>
                  </h3>
                  <div className="text-sm px-3 py-1 bg-blue-50 text-blue-700 rounded-full border border-blue-100">
                    {industry} Industry
                  </div>
                </div>
                
                {/* Industry Benchmarks Chart */}
                <div className="mt-4 h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart 
                      data={benchmarkChartData}
                      margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
                      barGap={0}
                      barCategoryGap="15%"
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis 
                        dataKey="category"
                        tick={{ fontSize: 11 }}
                        axisLine={{ stroke: '#E5E7EB' }}
                        tickLine={false}
                        interval={0}
                        angle={-45}
                        textAnchor="end"
                        height={70}
                      />
                      <YAxis 
                        domain={[0, 100]} 
                        unit="%" 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend 
                        verticalAlign="top"
                        height={36}
                        iconType="circle"
                      />
                      <Bar 
                        dataKey="your" 
                        name="Your Score" 
                        fill="#3B82F6" 
                        radius={[4, 4, 0, 0]}
                        maxBarSize={40}
                      />
                      <Bar 
                        dataKey="average" 
                        name="Industry Average" 
                        fill="#9CA3AF" 
                        radius={[4, 4, 0, 0]} 
                        maxBarSize={40}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="top" 
                        name="Top Performers" 
                        stroke="#10B981" 
                        strokeWidth={2}
                        dot={{ fill: '#10B981', r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-600">
                      This chart compares your scores across key dimensions with industry averages and top performers. 
                      Areas where the blue bars are below the gray bars represent opportunities for improvement 
                      relative to your industry.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <h3 className="font-medium text-gray-700 mb-3">Market Position</h3>
                <div className="flex items-center justify-between mb-4">
                  <div className="text-xl font-semibold text-blue-600">{analysis.position}</div>
                </div>
                
                <div className="mt-4">
                  <div className="mb-2 flex justify-between">
                    <span className="text-sm font-medium text-gray-600">Your BEAM Score</span>
                    <span className="text-sm font-medium text-gray-600">Industry Average</span>
                  </div>
                  <div className="h-7 w-full bg-gray-100 rounded-full relative">
                    <div 
                      className="h-7 bg-blue-500 rounded-full flex items-center"
                      style={{ width: `${(scoreComparison.your / 100) * 100}%` }}
                    >
                      <span className="absolute left-2 text-xs font-medium text-white">
                        {scoreComparison.your.toFixed(1)}
                      </span>
                    </div>
                    <div className="absolute top-1/2 transform -translate-y-1/2 right-2">
                      <span className="text-xs font-medium text-gray-700">
                        {scoreComparison.average.toFixed(1)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Market position interpretation */}
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                    <div className="flex gap-2">
                      <PieChart className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-blue-700">
                        {scoreComparison.your > scoreComparison.average + 10 ? (
                          <p>Your project is performing significantly above the industry average, positioning you as a leader in your category.</p>
                        ) : scoreComparison.your > scoreComparison.average ? (
                          <p>Your project is performing moderately above industry average, which gives you a competitive advantage.</p>
                        ) : scoreComparison.your > scoreComparison.average - 10 ? (
                          <p>Your project is performing near the industry average. Focus on differentiation to stand out from competitors.</p>
                        ) : (
                          <p>Your project is currently below industry average. Prioritize the recommendations to improve your competitive position.</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="h-5 w-5 text-green-500" />
                    <h3 className="font-medium text-gray-700">Strengths</h3>
                  </div>
                  <ul className="space-y-3">
                    {analysis.strengths.map((strength, index) => (
                      <li key={index} className="flex items-start gap-2 p-2 bg-green-50 rounded-lg border border-green-100">
                        <Award className="h-4 w-4 text-green-500 mt-0.5" />
                        <span className="text-gray-700">{strength}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {/* Strengths interpretation */}
                  <div className="mt-3 flex items-start gap-2">
                    <Lightbulb className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-gray-600">
                      These strengths represent your project's competitive advantages. 
                      Build your marketing and positioning strategy around these areas.
                    </p>
                  </div>
                </div>
                
                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingDown className="h-5 w-5 text-red-500" />
                    <h3 className="font-medium text-gray-700">Improvement Areas</h3>
                  </div>
                  <ul className="space-y-3">
                    {analysis.weaknesses.map((weakness, index) => (
                      <li key={index} className="flex items-start gap-2 p-2 bg-red-50 rounded-lg border border-red-100">
                        <span className="h-4 w-4 flex items-center justify-center rounded-full bg-red-100 text-red-500 mt-0.5 flex-shrink-0">
                          <span className="text-xs">!</span>
                        </span>
                        <span className="text-gray-700">{weakness}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {/* Weaknesses interpretation */}
                  <div className="mt-3 flex items-start gap-2">
                    <Lightbulb className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-gray-600">
                      Addressing these areas will strengthen your competitive position. 
                      Prioritize improvements based on potential market impact.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="h-5 w-5 text-purple-500" />
                  <h3 className="font-medium text-gray-700">Strategic Opportunities</h3>
                </div>
                <ul className="space-y-3">
                  {analysis.opportunities.map((opportunity, index) => (
                    <li key={index} className="flex items-start gap-2 p-2 bg-purple-50 rounded-lg border border-purple-100">
                      <span className="h-5 w-5 flex items-center justify-center rounded-full bg-purple-100 text-purple-600 flex-shrink-0">
                        {index + 1}
                      </span>
                      <span className="text-gray-700">{opportunity}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-gray-600">
                      <p className="mb-2">These industry-specific opportunities are aligned with your current strengths and market position.</p>
                      <p className="font-medium">Recommendation: Focus on 1-2 opportunities that best match your strategic objectives.</p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          
          {!analysis && (
            <div className="text-center py-6 text-gray-500">
              Insufficient data to perform competitive analysis
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CompetitiveAnalysis;
